import { FilterLang } from '../ParsingLangs/FilterLang';
import { SortLang } from '../ParsingLangs/SortLang';
import {
  editFieldSetting,
  editViewSetting,
  field,
  removeField,
  removeSort,
  removeView,
  removeFilter,
  view,
  layout,
  editSort,
} from '../tokens';
import {
  type AddFilterToken,
  type DatabaseSortDirection,
  type EditFilterToken,
  type ParsedRow,
  type SortToken,
  type Token,
  TokenType,
} from '../types';
import { stringToDatabaseLayoutType } from '../utils';

export function* parseEditMetadataRow(row: ParsedRow): Generator<Token> {
  switch (row['operation_type']) {
    case 'add_field':
      yield parseAddFieldOperation(row);
      break;
    case 'edit_field':
      yield parseEditFieldOperation(row);
      break;
    case 'delete_field':
      yield parseDeleteFieldOperation(row);
      break;
    case 'edit_view':
      yield parseEditViewOperation(row);
      break;
    case 'add_sort':
      yield* parseAddSortOperation(row);
      break;
    case 'edit_sort':
      yield parseEditSortOperation(row);
      break;
    case 'delete_sort':
      yield parseDeleteSortOperation(row);
      break;
    case 'delete_view':
      yield parseDeleteViewOperation(row);
      break;
    case 'add_filter':
      yield parseAddFilterOperation(row);
      break;
    case 'edit_filter':
      yield parseEditFilterOperation(row);
      break;
    case 'delete_filter':
      yield parseDeleteFilterOperation(row);
      break;
    case 'add_view':
      yield* parseAddViewOperation(row);
      break;
  }
}

function parseAddFieldOperation(row: ParsedRow) {
  switch (row.property) {
    case 'field_type':
      return field(row['target'], row['value']);
    default:
      return editFieldSetting(row['target'], row['property'], row['value']);
  }
}

function parseEditFieldOperation(row: ParsedRow) {
  return editFieldSetting(row['target'], row['property'], row['value']);
}

function parseEditViewOperation(row: ParsedRow) {
  return editViewSetting(row['target'], row['property'], row['value']);
}

function* parseAddSortOperation(row: ParsedRow): Generator<Token> {
  const viewName = row['target'];
  const sortsString = row['value'];

  if (!sortsString) {
    return;
  }

  try {
    const tokens: Omit<SortToken, 'viewName'>[] =
      SortLang.Statement.tryParse(sortsString);

    for (const token of tokens) {
      yield { ...token, viewName };
    }
  } catch (error) {
    console.error('Error creating sort tokens for edit.add_sort', error);
    return;
  }
}

function parseEditSortOperation(row: ParsedRow) {
  return editSort(
    row['target'],
    row['property'],
    row['value'].toLowerCase() as DatabaseSortDirection
  );
}

function parseDeleteSortOperation(row: ParsedRow) {
  return removeSort(row['target'], row['property']);
}

function parseDeleteFieldOperation(row: ParsedRow) {
  return removeField(row['target']);
}

function parseDeleteViewOperation(row: ParsedRow) {
  return removeView(row['target']);
}

function parseAddFilterOperation(row: ParsedRow): AddFilterToken {
  const viewName = row['target'];
  const filterString = row['value'];
  const parsedFilter = FilterLang.Statement.tryParse(filterString);

  return {
    ...parsedFilter,
    viewName,
  };
}

function parseEditFilterOperation(row: ParsedRow): EditFilterToken {
  const viewName = row['target'];
  const filterString = row['value'];
  const parsedFilter = FilterLang.Statement.tryParse(filterString);

  return {
    ...parsedFilter,
    tokenType: TokenType.EDIT_FILTER,
    viewName,
  };
}

function parseDeleteFilterOperation(row: ParsedRow) {
  return removeFilter(row['target']);
}

function* parseAddViewOperation(row: ParsedRow): Generator<Token> {
  yield view(row['target']);

  if (row['property']) {
    const layoutType = stringToDatabaseLayoutType(row['property']);
    if (layoutType) {
      yield layout(layoutType, row['target']);
    }
  }
}
